import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../PageLayout';

const HouseproBookingFlow = ({ onClose }) => {
  const navigate = useNavigate();
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);
  
  // Configuration
  const HCP_TOKEN = process.env.REACT_APP_HCP_TOKEN;
  const HCP_ORG_NAME = process.env.REACT_APP_HCP_ORG_NAME;
  const SCRIPT_URL = `https://online-booking.housecallpro.com/script.js?token=${HCP_TOKEN}&orgName=${HCP_ORG_NAME}`;

  // Load guest info from localStorage if available
  useEffect(() => {
    const storedGuestInfo = localStorage.getItem('hcp_guest_info');
    if (storedGuestInfo) {
      try {
        const parsed = JSON.parse(storedGuestInfo);
        setGuestInfo(parsed);
        console.log('✅ Loaded guest info for HCP widget:', parsed);
      } catch (error) {
        console.warn('⚠️ Failed to parse stored guest info:', error);
      }
    }
  }, []);
  


  // Load the HCP script and auto-open widget
  useEffect(() => {
    let script = null;
    let scriptLoadRetries = 0;
    const MAX_RETRIES = 3;

    // Add CSS overrides to make widget larger
    const addWidgetCSS = () => {
      const style = document.createElement('style');
      style.textContent = `
        .hcp-widget-container,
        .hcp-modal,
        .hcp-widget-modal {
          width: 90vw !important;
          height: 90vh !important;
          max-width: 1200px !important;
          max-height: 800px !important;
        }
        .hcp-widget-container iframe,
        .hcp-modal iframe,
        .hcp-widget-modal iframe {
          width: 100% !important;
          height: 100% !important;
        }
      `;
      document.head.appendChild(style);
    };

    const openWidget = () => {
      if (window.HCPWidget) {
        console.log('✅ Opening Housecall Pro widget');
        
        // If we have guest info, try to pass it to the widget
        if (guestInfo) {
          console.log('📋 Pre-populating HCP widget with guest info');
          // Note: HCP widget API may support pre-filling data via configuration
          // This would depend on their specific API - for now we'll just log it
          // In a real implementation, you might pass this via widget configuration
        }
        
        // Try to configure widget size if supported
        if (window.HCPWidget.openModal) {
          // Check if the widget supports configuration options
          window.HCPWidget.openModal({
            size: 'large', // or 'fullscreen', 'medium', etc.
            width: '90vw',
            height: '90vh',
            fullscreen: true // Try fullscreen option if available
          });
        } else {
          window.HCPWidget.openModal();
        }
        setIsWidgetOpen(true);
        
        // Clean up stored guest info after widget opens
        localStorage.removeItem('hcp_guest_info');
      } else {
        console.warn('⚠️ HCPWidget not available yet');
        if (scriptLoadRetries < MAX_RETRIES) {
          scriptLoadRetries++;
          setTimeout(openWidget, 1000);
        } else {
          console.error('❌ Failed to load HCPWidget after retries');
          navigate('/');
        }
      }
    };

    const loadHCPScript = () => {
      script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        console.log('✅ Housecall Pro widget script loaded');
        // Add CSS overrides for larger widget
        addWidgetCSS();
        // Add global event listener for widget close
        window.addEventListener('message', (event) => {
          if (event.data === 'hcp-widget-closed') {
            console.log('Widget closed via message event');
            setIsWidgetOpen(false);
            navigate('/');
          }
        });
        setTimeout(openWidget, 500);
      };
      script.onerror = (error) => {
        console.error('❌ Error loading Housecall Pro widget script:', error);
        navigate('/');
      };
      document.body.appendChild(script);
    };

    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (!existingScript) {
      loadHCPScript();
    } else {
      openWidget();
    }

    // Cleanup function
    return () => {
      if (script) {
        document.body.removeChild(script);
      }
      // Clean up any HCP widget elements
      const widgetElements = document.querySelectorAll('.hcp-widget-container');
      widgetElements.forEach(el => el.remove());
      // Remove global event listener
      window.removeEventListener('message', () => {});
    };
  }, [navigate]);

  // Listen for widget events
  useEffect(() => {
    const checkWidgetInterval = setInterval(() => {
      // If widget was open but is no longer visible, navigate home
      if (isWidgetOpen && !document.querySelector('.hcp-widget-container')) {
        console.log('Widget no longer visible, navigating home');
        setIsWidgetOpen(false);
        navigate('/');
        clearInterval(checkWidgetInterval);
      }
    }, 500);

    return () => {
      clearInterval(checkWidgetInterval);
    };
  }, [isWidgetOpen, navigate]);

  // Listen for ESC key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && isWidgetOpen) {
        console.log('ESC pressed, navigating home');
        setIsWidgetOpen(false);
        navigate('/');
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isWidgetOpen, navigate]);

  return (
    <PageLayout hideAuth={true}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        p: 3 
      }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading booking system...
        </Typography>
        
        {guestInfo && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Welcome {guestInfo.firstName}! Your information will be pre-filled.
          </Typography>
        )}

        {/* Hidden container for widget */}
        <Box id="hcp-widget-container" sx={{ display: 'none' }} />
      </Box>
    </PageLayout>
  );
};

export default HouseproBookingFlow; 